import Vue from 'vue';

const state = {
  electorateName: "",
  electorateGrade: "",
  speciesData: {
    dbSpeciesId: 0,
    name: "",
    commonName: "",
    picture: {
      fields: {
        file: {
          url: ""
        }
      }
    },
    threats: [],
    threatStatus: "",
    fact: "",
    speciesCount: 0,
    languageTerm: "",
    languageGroup: "",
    languageExtraInfo: "",
  },
  selectedAddress: "",
  addressCoordinates: { lat: 0, lng: 0 },

  openDrawer: false,

  // info popup
  openInformationPopup: false,
  popupTitle: "",
  popupDescription: "",

  // Other species
  otherSpecies: [],
  // Other species popup
  openOtherSpeciesPopup: false,

  // Modal
  openModal: false,
  modalType: null,

  mps: null,
  numExtinct: 0,
};

const getters = {
  electorateName: (state) => state.electorateName,
  electorateGrade: (state) => state.electorateGrade,
  speciesData: (state) => state.speciesData,
  selectedAddress: (state) => state.selectedAddress,
  addressCoordinates: (state) => state.addressCoordinates,
  openDrawer: (state) => state.openDrawer,
  openInformationPopup: (state) => state.openInformationPopup,
  otherSpecies: (state) => state.otherSpecies,
  openOtherSpeciesPopup: (state) => state.openOtherSpeciesPopup,
  openModal: (state) => state.openModal,
  modalType: (state) => state.modalType,
  popupTitle: (state) => state.popupTitle,
  popupDescription: (state) => state.popupDescription,
  mps: (state) => state.mps,
  numExtinct: (state) => state.numExtinct,
};

const mutations = {
  updateElectorateName(state, electorateName) {
    if (state.electorateName !== electorateName) {
      state.electorateName = electorateName;
    }
  },

  updateElectorateGrade(state, electorateGrade) {
    if (state.electorateGrade !== electorateGrade) {
      state.electorateGrade = electorateGrade;
    }
  },

  updateSpeciesData(state, speciesData) {
    if (state.speciesData !== speciesData) {
      state.speciesData = speciesData;
    }
  },

  updateAddress(state, selectedAddress) {
    if (state.selectedAddress !== selectedAddress) {
      state.selectedAddress = selectedAddress;
    }
  },

  updateAddressCoordinates(state, addressCoordinates) {
    if (state.addressCoordinates !== addressCoordinates) {
      state.addressCoordinates = addressCoordinates;
    }
  },

  updateOpenDrawer(state, openDrawer) {
    if (state.openDrawer !== openDrawer) {
      state.openDrawer = openDrawer;
    }
  },

  updateOpenInformationPopup(state, openInformationPopup) {
    state.openModal = false;
    if (state.openInformationPopup !== openInformationPopup) {
      state.openInformationPopup = openInformationPopup;
    }
  },

  updateOtherSpecies(state, otherSpecies) {
    state.otherSpecies = otherSpecies
  },

  updateOpenOtherSpeciesPopup(state, openOtherSpeciesPopup) {
    state.openModal = false
    state.openInformationPopup = false
    if (state.openOtherSpeciesPopup !== openOtherSpeciesPopup) {
      state.openOtherSpeciesPopup = openOtherSpeciesPopup
    }
  },

  closeModal(state) {
    state.openModal = false;
    state.modalType = null;
  },

  openModal(state, type) {
    state.openInformationPopup = false;
    state.openModal = true;
    state.modalType = type;
  },

  updatePopupTitle(state, popupTitle) {
    if (state.popupTitle !== popupTitle) {
      state.popupTitle = popupTitle;
    }
  },

  updatePopupDescription(state, popupDescription) {
    if (state.popupDescription !== popupDescription) {
      state.popupDescription = popupDescription;
    }
  },

  setMps(state, value) {
    if (value != undefined) {
      state.mps = value;
    }
  },

  updateNumExtinct(state, numExtinct) {
    if(state.numExtinct !== numExtinct) {
      state.numExtinct = numExtinct;
    }
  },
};

const actions = {
  /**
   * Geocode an address to get matching coordinates
   * @param {*} payload string address
   * @returns object {lat: string, lng: string}
   */
  geocodeAddress(_, payload) {
    return new Promise((resolve, reject) => {
      try {
        Vue.http.post(`${wwfconfigs.WEB_API}/species-map`, { address: payload }).then(async response => {
          resolve(response.body);
        }, error => {
          reject(error);
        });
      } catch (err) {
        reject(err);
      }
    });
  },

  /**
   * Get postcode matching sent coordinates
   * @param {*} payload {lat: string, lng: string};
   * @returns string postcode that matches the coordinates
   */
  getPostcode(_, payload) {
    return new Promise((resolve, reject) => {
      try {
        Vue.http.get(`${wwfconfigs.WEB_API}/species-map/postcode?lat=${payload.lat}&lng=${payload.lng}`).then(async response => {
          resolve(response.body);
        }, error => {
          reject(error);
        });
      } catch (err) {
        reject(err);
      }
    });
  },

  /**
   * Get all electorates in a postcode
   * @param {*} payload string postcode
   * @returns object {electoraldivisionname: string, postcode: string}
   */
  getElectorates(_, payload) {
    return new Promise((resolve, reject) => {
      try {
        Vue.http.get(`${wwfconfigs.WEB_API}/species-map/electorates?postcode=${payload}`).then(async response => {
          resolve(response.body);
        }, error => {
          reject(error);
        });
      } catch (err) {
        reject(err);
      }
    });
  },

  /**
   * Get selected electorates grades.
   *
   * If no electorates are sent in payload, fetch all grades
   * @returns list of electorates names and their grades
   */
  getElectoratesGrade(_, payload) {
    return new Promise((resolve, reject) => {
      try {
        let query = '?';
        let count = 0;
        for (const electorate of payload) {
          query += `electorates${count}=${electorate}&`;
          count++;
        }

        Vue.http.get(`${wwfconfigs.WEB_API}/species-map/indicators${query}`).then(async response => {
          resolve(response.body);
        }, error => {
          reject(error);
        });
      } catch (err) {
        reject(err);
      }
    });
  },

  async getHeroSpecies(_, payload) {
    return new Promise((resolve, reject) => {
      try {
        const params = new URLSearchParams(payload);

        Vue.http.get(`${wwfconfigs.WEB_API}/species-map/hero-species?${params.toString()}`).then(async response => {
          resolve(response.body);
        }, error => {
          reject(error);
        });
      } catch (err) {
        reject(err);
      }
    });
  },

  async getSpecies(_, payload) {
    return new Promise((resolve, reject) => {
      try {
        // Construct query base on payload key:value pair
        let query = '?';
        for (const [key, value] of Object.entries(payload)) {
          query += `${key}=${value}&`;
        }

        // Fetch species
        Vue.http.get(`${wwfconfigs.WEB_API}/species-map/species${query}`).then(async response => {
          resolve(response.body);
        }, error => {
          reject(error);
        });
      } catch (err) {
        reject(err);
      }
    });
  },

  getDistinctSpecies() {
    return new Promise((resolve, reject) => {
      try {
        // Fetch species
        Vue.http.get(`${wwfconfigs.WEB_API}/species-map/distinct-species`).then(async response => {
          resolve(response.body);
        }, error => {
          reject(error);
        });
      } catch (err) {
        reject(err);
      }
    });
  },

  async getMp({ getters, dispatch }, electorate) {
    if (!getters.mps) {
      await dispatch('getMPs');
    }

    for (const mp of getters.mps) {
      if (mp.electorate && mp.electorate.startsWith(electorate)) {
        return mp;
      }
    }
  },

  getMPs({ commit }) {
    return new Promise((resolve, reject) => {
      try {
        // Fetch species
        Vue.http.get(`${wwfconfigs.WEB_API}/mps`,).then(response => {
          commit('setMps', response.body);
          resolve(response.body);
        }, error => {
          reject(error);
        });
      } catch (err) {
        reject(err);
      }
    });
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};



// WEBPACK FOOTER //
// ./src/store/modules/species-map.js